import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {NG_VALUE_ACCESSOR} from "@angular/forms";
import lodash, {DebouncedFunc} from 'lodash';
import {AbstractValueAccessorComponent} from "../../../common/component/value-accessor.component";
import {filterByTerm} from "../../../common/utils";

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MultiSelectComponent), multi: true}
  ],
  host: {'class' : 'input-group'}
})
export class MultiSelectComponent extends AbstractValueAccessorComponent<any> implements AfterViewInit, OnDestroy {
  private filterTerm: string = "";

  @Input() selectedItems: any[] = [];
  private outputSubject = new Subject<any[]>();
  private outputObservable = this.outputSubject.asObservable();
  private _inputBuffer: number = 0;

  @Input() disabled: boolean;
  @Input() itemTemplate;
  @Input() formatter;
  @Input() newValueTemplate : TemplateRef<any>;

  @ViewChild("dropdownToggle") dropdownToggle: ElementRef<HTMLElement>;
  @ViewChild("dropdownReference") dropdownReference: ElementRef<HTMLElement>;
  private activeDropdownReference: HTMLElement;
  private debouncedFilter: DebouncedFunc<any> = lodash.debounce(this.emitUpdated, this._inputBuffer);

  ngAfterViewInit() {
    this.activeDropdownReference = this.dropdownReference.nativeElement;
    this.dropdownToggle.nativeElement.addEventListener('hidden.bs.dropdown', this.resetDropdownReference);
    (window as any).Dropdown.getOrCreateInstance(this.dropdownToggle.nativeElement, {
      reference: {
        contextElement: this.dropdownToggle.nativeElement,
        getBoundingClientRect: () => this.getActiveDropdownReference().getBoundingClientRect()
      }
    });
  }

  ngOnDestroy() {
    this.dropdownToggle?.nativeElement.removeEventListener('hidden.bs.dropdown', this.resetDropdownReference);
  }

  private getActiveDropdownReference(): HTMLElement {
    return this.activeDropdownReference?.isConnected
      ? this.activeDropdownReference
      : this.dropdownReference.nativeElement;
  }

  private resetDropdownReference = (): void => {
    this.activeDropdownReference = this.dropdownReference.nativeElement;
  };

  private closeDropdown(): void {
    (window as any).Dropdown.getInstance(this.dropdownToggle.nativeElement)?.hide();
  }

  setDropdownReference(reference: HTMLElement): void {
    this.activeDropdownReference = reference;
  }

  toggleDropdownFromSelectedItem(event: MouseEvent, reference: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDropdownReference(reference);
    (window as any).Dropdown.getInstance(this.dropdownToggle.nativeElement)?.toggle();
  }

  get inputModel(): any {
    return this.filterTerm;
  }

  @Input() set inputModel(value: string) {
    this.filterTerm = value;
    this.termChanged.emit(value);
    this.onUpdate();
  }

  @Input() filterFunction: (term) => (value : any) => boolean = t => filterByTerm(t);

  @Input()
  set data(value: any[] | Observable<any[]>) {
    if (lodash.isArray(value)) {
      this.input = value;
      this.onUpdate();
    } else {
      this.searching = true;
      value?.subscribe({
        next: (r) => {
          this.input = r;
          this.searching = false;
          this.onUpdate();
        },
        error: (e) => this.searching = false
      });
    }
  }

  @Input() placeholder = "";

  @Output() updated = new EventEmitter<any[]>();
  @Output() termChanged = new EventEmitter<string>();
  @Output() deleted: EventEmitter<any> = new EventEmitter<any>();

  @Input() set inputBufferTime(value: number) {
    this._inputBuffer = value;
    this.debouncedFilter = lodash.debounce(this.emitUpdated, this._inputBuffer);
  }

  searching: boolean;
  private input: any[] = [];

  onInput = (text$: Observable<string>) => {
    text$.subscribe(term => {
      this.inputModel = term;
    });
    return this.outputObservable;
  };

  private onUpdate() {
    this.filterTerm ? this.debouncedFilter() : this.emitUpdated();
  }

  clearSelection = () => {
    this.closeDropdown();
    this.deleted.emit(this.selectedItems);
    this.selectedItems = [];
  }

  deleteValue(value: any) {
    this.closeDropdown();
    this.selectedItems = this.selectedItems.filter(v => v !== value);
    this.deleted.emit(value);
  }

  removeSelectedItemWhenEmpty = () => {
    if (!lodash.isEmpty(this.filterTerm)) {
      return;
    }
    if (this.selectedItems.length > 0) {
      this.closeDropdown();
      const lastItem = this.selectedItems[this.selectedItems.length-1];
      this.selectedItems = this.selectedItems.filter(t => t !== lastItem);
      this.deleted.emit(lastItem);
    }
  }

  onSelect = (value) => {
    if (value) {
      this.selectedItems.push(value);
    }
  };

  get value(): any {
    return this.selectedItems;
  }

  writeValue(value: any): void {
    this.selectedItems = value === undefined ? null : value;
  }

  private emitUpdated() {
    return this.updated.emit(this.input.filter(this.filterFunction(this.filterTerm)));
  }
}
